/**
 * Put a roster member on the Stage.
 *
 * Live mounts a player; not live but with a recent clip mounts that instead — a
 * member with SOMETHING to watch must not read the same as one with nothing,
 * which is what `player: undefined` for both cases did.
 *
 * Split out of `DropHubSurface`: seventy lines of occupant construction with
 * four separate "why this shape" notes, in a file already past the 400-line
 * ceiling its own header cites.
 */
import React from "react";
import {StreamInlinePlayer} from "@/widgets/StreamInlinePlayer";
import {playerSource as rosterPlayerSource, highlightForMember} from "@/widgets/rosterMember";
import type {SurfaceStageOccupant} from "@/surfaces/spine/SurfaceStage";

type RosterMemberLike = Parameters<typeof rosterPlayerSource>[0] & {
    isLive?: boolean;
    thumbnailUrl?: string;
    viewerCount?: number;
    label?: string;
    streamTitle?: string;
    gameName?: string;
    effectiveLiveState?: string;
};

/** Build the Stage occupant for a member, or `undefined` when there is no member. */
export function memberStageOccupant(
    m: RosterMemberLike | undefined | null,
    highlights: Parameters<typeof highlightForMember>[1],
): SurfaceStageOccupant | undefined {
    if (!m) return undefined;
    const clip = !m.isLive ? highlightForMember(m, highlights) : undefined;
    /*
     * THE HELPER, NOT `m.playerSource` SPREAD RAW.
     *
     * Some featured streams persist Twitch's numeric channel id instead of
     * the login the embed keys on, which loads a black screen. The server
     * resolves that (falling back to the Twitch social's username) for
     * every other Drop-In card; `rosterPlayerSource()` reads that same
     * resolved field. Spreading `m.playerSource` here reintroduced exactly
     * the bug this helper exists to fix.
     */
    const source = m.isLive ? rosterPlayerSource(m) : undefined;
    /*
     * NO SOURCE AND NO CLIP IS NOT AN OCCUPANT. Without this the stage fell
     * back to the member's own photo behind a play button — a portrait
     * presented as something to watch, for a finalist who has published
     * nothing.
     */
    if (!source && !clip) return undefined;
    return {
        player: source
            /*
             * EVERYTHING GOES ON THE SOURCE. `StreamInlinePlayer` takes one
             * prop — `title`, `muted`, `autoplay` and `viewerInitiated` are
             * all fields of `InlinePlayerSource`, and the embed builder
             * reads them from there. Passed at the top level they were
             * silently ignored: the iframe was composed with `autoplay=0`,
             * so a viewer who pressed play got a poster and a second play
             * button, and the watch session never recorded intent.
             */
            ? <StreamInlinePlayer
                source={{
                    ...source,
                    autoplay: true,
                    /* Browsers refuse unmuted autoplay — the gesture was on our card, not in the iframe. */
                    muted: true,
                    viewerInitiated: true,
                }}
            />
            /*
             * An <iframe>, not `StreamInlinePlayer` — that component mounts
             * a LIVE channel embed, and a clip is a different object with
             * its own embed URL. Not autoplayed: muted autoplay on a stage
             * a viewer just navigated onto is one autoplaying thing too
             * many. `parent` is required for the Twitch clip embed to
             * render at all off twitch.tv — same requirement as the legacy
             * `CreatorTheaterPane` clip pane this mirrors.
             */
            : clip
                ? <iframe
                    title={clip.title}
                    src={`${clip.embedUrl}&parent=${window.location.hostname}&autoplay=false`}
                    allowFullScreen
                    frameBorder="0"
                    scrolling="no"
                />
                : undefined,
        imageUrl: clip?.thumbnailUrl ?? m.thumbnailUrl,
        live: m.isLive,
        /* Live only — a clip/VOD has no room to talk in, so EXPANDED stays refused. */
        chat: source?.twitchChannelId
            ? {platform: "TWITCH", channel: source.twitchChannelId}
            : source?.kickChannelSlug
                ? {platform: "KICK", channel: source.kickChannelSlug}
                : undefined,
        viewers: m.isLive && m.viewerCount ? `${m.viewerCount.toLocaleString()} watching` : undefined,
        title: clip ? clip.title : m.label ?? "",
        /*
         * "UNKNOWN" IS NOT "OFFLINE" — see the same note in `rosterMembers`.
         * A clip gets its own explicit label so it can never be mistaken
         * for a live stream, which is the one thing a recorded clip must
         * never pretend to be.
         */
        subtitle: m.isLive
            ? m.streamTitle
            : clip
                ? `Clip · ${m.label}`
                : m.effectiveLiveState === "UNKNOWN"
                    ? "Live status unavailable"
                    : m.gameName ? `Offline · last live playing ${m.gameName}` : "Offline",
    };
}
